<template>
  <div  v-if="attributeList && attributeList.length > 0">
    <template v-if="readOnly">
      <SubHeader :text="$t('HOST_VALUE_CHAIN_ATTRIBUTES')" />
    </template>
    <template v-else>
      <SubHeader :text="$t('VALUE_CHAIN_ATTRIBUTES')" />
    </template>
    
    <Box max-width="100%" padding=".5em">  
      <ul class="attribute-list" :class="`${!readOnly? 'attribute-list__editable': 'attribute-list__readonly'}`">
        <template v-if="readOnly">
          <li v-for="(attribute, index) in attributeList" :key="index">
              <strong>{{ attributeName(attribute) }}:</strong> 
              <template v-if="!isJsonAttribute(attribute.name) || attributeValue(attribute) === '-'">
                <span>{{ attributeValue(attribute) }}</span>
              </template>
              <v-btn small icon @click="showDialog(attribute)" v-else>
                <v-icon small>mdi-information-outline</v-icon>
              </v-btn> 
          </li>  
        </template>
        <template v-else>
          <li v-for="(attribute, index) in attributeList" :key="index" :style="isTransient(attribute)? `background: #f0f0f0; border-radius: 10px; margin-right: .5em; padding-left: .5em`: ''">
            <template v-if="['string', 'number'].includes(attribute.type)">
              <BoxItem dense :additionalSmallHeading="attributeOwnerLabel(attribute)" 
                :title="attributeName(attribute)" :value="attribute.pivot.value" @updateValue="(e) => updateAttribute(attribute, e)" />
            </template>
            <template v-else-if="['boolean'].includes(attribute.type)">
              <BoxItem dense :title="attributeName(attribute)" :additionalSmallHeading="attributeOwnerLabel(attribute)"  valueType="boolean" :value="attribute.pivot.value" type="switch"  @updateValue="(e) => updateAttribute(attribute, e)" />
            </template>

            <template v-else-if="['enum'].includes(attribute.type)">
              <BoxItem dense customStyle="width: 200px;"  forceSelectType :additionalSmallHeading="attributeOwnerLabel(attribute)" :title="attributeName(attribute)" :items="attribute.settings && attribute.settings.options || []"  :value="attribute.pivot.value"   @updateValue="(e) => updateAttribute(attribute, e)" />
            </template>
            <v-tooltip top v-if="isTransient(attribute)" :color="transientColor" >
              <template v-slot:activator="{ on, attrs }">
                <v-icon
                  :color="transientColor"
                  v-bind="attrs"
                  v-on="on"
                  class="mr-2"
                  style="margin-left: -12px;"
                >
                mdi-information-outline
                </v-icon>
              </template>
              <span><strong>{{ $t('ATTRIBUTE_TRANSIENT_LABEL') }}:</strong> <span>{{ attribute.pivot.transient }}</span></span>
            </v-tooltip>
              
          </li>
        </template>
      </ul>
    </Box>

    <ValueChainAttributeDialog :visible="dialogVisible" :body="dialogBody" :title='dialogTitle' @close="closeDialog" />
</div>
</template>

<script>
import Box from "../../components/ui-elements/Box";
import BoxItem from "../ui-elements/BoxItem.vue";
import SubHeader from "@/components/ui-elements/SubHeader.vue";
import {cloneDeep} from 'lodash';
import ValueChainAttributeDialog from "./ValueChainAttributeDialog.vue";

  export default {
    name: "ValueChainAttributes",
    props:{
      item: {
        type: Object,
        required: true
      },
      readOnly:{
        type: Boolean,
        default: true
      },
      items: {
        type: Array,
        default: () => []
      },
      bundle: {
        type: Object,
        default: null
      }
    },
    components: { SubHeader, Box, ValueChainAttributeDialog, BoxItem },
    data(){
      return {
        show: false, 
        attributeList: [],
        dialogVisible: false,
        dialogBody: '',
        dialogTitle: '',
        defaultJsonAttributeList: [
          'emptiesArticles',
          'mixedArticles',
          'customProperties'
        ],
        jsonAttributeList: [],
        transientColor: '#1b99a7'
      }
    },
    watch: {
      readyToSetValueChain: {
        handler() {
          this.setValueChainAttributes();
        },
        deep: true,
        immediate: true
      },
      bundle: {
        handler() {
          this.setValueChainAttributes();
        },
        deep: true,
      },
      
    },
    computed:{
      currentLangShortCode() {
        return this.$store.getters.langShortcode;
      },
      readyToSetValueChain(){
        if(this.bundle === null) return this.item && Object.keys(this.item).length >0 && this.items && this.items.length > 0;
        return this.items && this.items.length > 0 && this.bundle !== null;
      },
      bundleAttributes(){
        if(this.bundle === null) return [];
        return this.bundle.attributesDetails.map((el) => {
          return el.attribute;
        });
      },
      owners(){
        return [
          {value: 'HOST', label: this.$i18n.t('ATTRIBUTE-OWNER-HOST')},
          {value: 'BG', label: this.$i18n.t('ATTRIBUTE-OWNER-BG')},
          {value: 'SITE', label: this.$i18n.t('ATTRIBUTE-OWNER-SITE')},
        ]
      },
      attributeOwnerLabel(){
        return (attribute) => {
          const owner = this.owners.find((el) => el.value === attribute.owner);
          if(!owner) return '';
          return owner.label;
        }
      },
      isTransient(){
        return (attribute) => {
          return attribute.pivot && attribute.pivot.transient;
        }
      }

    },

    methods:{
      attributeName(attribute){
        let name = '';
        if(attribute.displayName && attribute.displayName[this.currentLangShortCode]){
          name = attribute.displayName[this.currentLangShortCode];
        }
        if(!name || !name.length){
          name += ` ${attribute.name}`
        }
        
        return name;
      },
      attributeValue(attribute){
        if(attribute.pivot === undefined){
          attribute.pivot = {value: null};
        }
        if(attribute.isWritable && attribute.pivot.override != null){
          return attribute.pivot.override;
        }
        if(attribute.pivot.value === null) return '-';
        return attribute.pivot.value;
      },
      setValueChainAttributes(){
        if(this.bundle === null){
          this.setItemValueChainAttributes();
        }else{
          this.setBundleValueChainAttributes();
        }
      },

      setBundleValueChainAttributes(){
        this.attributeList = [];
        if(this.readOnly){
          let list = cloneDeep(this.items).filter((attr) => Boolean(attr.isBundleAttribute) && Boolean(attr.isReadOnlyHostAttribute) && (Boolean(attr.isValueChain) || Boolean(attr.isHostAttribute)));
          if(this.bundleAttributes && this.bundleAttributes.length >0){
            const bundleAttributes = cloneDeep(this.bundleAttributes).filter((attr) => Boolean(attr.isBundleAttribute) && Boolean(attr.isReadOnlyHostAttribute) && (Boolean(attr.isValueChain) || Boolean(attr.isHostAttribute)));
            list = this.prepareAttributeList(list, bundleAttributes);
          }else{
            list = this.prepareAttributeList(list, []);
          }    
          this.attributeList = cloneDeep(list);
        }else{
          let list = cloneDeep(this.items).filter((attr) => Boolean(attr.isBundleAttribute) && Boolean(attr.isWritableCustomAttribute) && (Boolean(attr.isValueChain) || Boolean(attr.isHostAttribute)));
          if(this.bundleAttributes && this.bundleAttributes.length >0){
            
            const bundleAttributes = cloneDeep(this.bundleAttributes).filter((attr) => Boolean(attr.isBundleAttribute) && Boolean(attr.isWritableCustomAttribute) && (Boolean(attr.isValueChain) || Boolean(attr.isHostAttribute)));
            list = this.prepareAttributeList(list, bundleAttributes);
          }else{
            list = this.prepareAttributeList(list, []);
          }  
          this.attributeList = cloneDeep(list);
        }
      },

      
      setItemValueChainAttributes(){
        this.attributeList = [];
        if(this.readOnly){
          let list = cloneDeep(this.items).filter((attr) => Boolean(attr.isItemAttribute) && Boolean(attr.isReadOnlyHostAttribute));
          if(this.item.meta && this.item.meta.length >0){
            const metaAttributes = cloneDeep(this.item.meta).filter((attr) => Boolean(attr.isItemAttribute) && Boolean(attr.isReadOnlyHostAttribute));
            list = this.prepareAttributeList(list, metaAttributes);
          }else{
            list = this.prepareAttributeList(list, []);
          }    
          this.attributeList = cloneDeep(list);
        }else{
          let list = cloneDeep(this.items).filter((attr) => Boolean(attr.isItemAttribute) && Boolean(attr.isWritableCustomAttribute));
          if(this.item.meta && this.item.meta.length >0){
            const metaAttributes = cloneDeep(this.item.meta).filter((attr) => Boolean(attr.isItemAttribute) && Boolean(attr.isWritableCustomAttribute));
            list = this.prepareAttributeList(list, metaAttributes);
          }else{
            list = this.prepareAttributeList(list, []);
          }  
          this.attributeList = cloneDeep(list);
        }
      },

      prepareAttributeList(defaultAttributeList, metaAttributeList){
        return cloneDeep(defaultAttributeList).map((attr) => {
          if(metaAttributeList && metaAttributeList.length > 0){
            const foundAttr = metaAttributeList.find(el => el.name === attr.name);
            if(foundAttr && foundAttr.pivot !== undefined){
              attr.pivot = foundAttr.pivot;
            }else{
              attr.pivot = {
                value: null,
                override: null
              }
            }
          }else{
            attr.pivot = {
                value: null,
                override: null
              }
          }
          return attr;
        });
      },

      isJsonAttribute(attributeName){
        return this.defaultJsonAttributeList.includes(attributeName);
      },
      showDialog(attribute){
        this.dialogTitle = this.$t(attribute.name);
        this.dialogBody = this.attributeValue(attribute);
        this.dialogVisible = true;
      },
      closeDialog(){
        this.dialogTitle = '';
        this.dialogBody = '';
        this.dialogVisible = false;
      },
      updateAttribute(attribute, newValue){
        if(this.bundle === null){
          this.updateItemAttribute(attribute, newValue);
        }else{
          this.updateBundleAttribute(attribute, newValue);
        }
      },

      updateBundleAttribute(attribute, newValue){
        if(attribute.type === 'boolean'){
          attribute.pivot.value = newValue === true || newValue === '1'? 1: 0;
        }else{
          attribute.pivot.value = newValue;
        }
        this.$emit('updateBundleAttribute', cloneDeep(attribute));
      },

      updateItemAttribute(attribute, newValue){
        if(attribute.type === 'boolean'){
          attribute.pivot.value = newValue === true || newValue === '1'? '1': '0';
        }else{
          attribute.pivot.value = newValue;
        }
        const item = cloneDeep(this.item);
        if(!item.meta || !item.meta.length){
          item.meta = [];
        }
        const attrIndex = item.meta.findIndex(el => el.name === attribute.name);
        if(attrIndex === -1) {
          item.meta.push(cloneDeep(attribute));
        }else{
          item.meta[attrIndex] = cloneDeep(attribute);
        }
        this.$emit('updateValueChainAttributes', cloneDeep(item.meta));
      }
      
    }
  }
</script>

<style lang="scss" scoped>
  .attribute-list{
    
    list-style: none;
    padding-left: 0;
    width: 100%;
    display: inline-flex; 
    flex-wrap: wrap;
    &__editable{
      & > li{
        position: relative;
        display: flex;
        // flex: 1;
        // padding: 0.05em .35em;
        & > *{
          // padding: .1em;
          font-family: Arial, sans-serif;
          font-size: 14px!important;
          line-height: 14px!important;
          text-align: center;
          color: #666666;
        }
      }
      
    }

    &__readonly{
      & > li{
        position: relative;
        // flex: 1;
        padding: 0.05em .35em;
        & > *{
          padding: .1em;
          font-family: Arial, sans-serif;
          font-size: 14px;
          line-height: 18px;
          text-align: center;
          color: #666666;
        }
      }
    }

    
  }
</style>
